// @ts-nocheck
import React, { lazy } from 'react';
import { 
    CheckmarkIcon, 
    GoodieWhiteIcon, 
    SparklesIcon, 
    TrophyIcon, 
    CalendarIcon, 
    StoreIcon, 
    BellIcon,
    AudioIcon
} from '../components/Icons';

export interface ModDefinition {
    id: string;
    name: string;
    icon: React.ComponentType<{ className?: string }> | string;
    component: React.LazyExoticComponent<React.ComponentType<any>>;
    route?: string;
    description: string;
    author?: string;
    themeColor?: string;
    visibilityControl?: 'force_public' | 'force_hidden' | 'user_choice';
}

export const MOD_REGISTRY: Record<string, ModDefinition> = {
    'auxbox': {
        id: 'auxbox',
        name: 'AuxBox',
        icon: AudioIcon,
        component: lazy(() => import('../components/AuxBoxMod').then(m => ({ default: m.AuxBoxMod }))),
        route: '/auxbox',
        description: 'Your unified local audio and streaming hub. Organize local files, paths, and GoodLyfe audio posts into custom albums, artists, and playlists.',
        author: 'vibeman1',
        themeColor: '#3b82f6'
    },
    'todo': {
        id: 'todo',
        name: 'Bzzt Bzzt',
        icon: '/assets/todo_icon.png',
        component: lazy(() => import('../components/TodoMod').then(m => ({ default: m.TodoMod }))),
        route: '/todo',
        description: 'A modular notification maker that keeps you on track with Bzzt notifications.',
        author: 'vibeman1',
        themeColor: '#000000',
        visibilityControl: 'user_choice'
    },
    'vibeman1_footprint': {
        id: 'vibeman1_footprint',
        name: 'Daily Footprint',
        icon: '/assets/footprint_logo.png',
        component: lazy(() => import('../components/FootprintPage').then(m => ({ default: m.FootprintPage }))),
        route: '/footprint',
        description: 'Track your daily impact, earn Life Points, and compete with your connections.',
        author: 'vibeman1',
        themeColor: '#000000',
        visibilityControl: 'force_public'
    },
    'pimp_my_ride': {
        id: 'pimp_my_ride',
        name: 'Pimp My Ride',
        icon: '/assets/goodie_store_icon.png',
        component: lazy(() => import('../components/PimpMyRide').then(m => ({ default: m.PimpMyRide }))),
        route: '/pimp-my-ride',
        description: 'The premier boutique for modular experiences and customizations. Gifted to you by GoodieBoy.',
        author: 'system'
    },
    'art_class': {
        id: 'art_class',
        name: 'Art Class',
        icon: SparklesIcon,
        component: lazy(() => import('../components/ArtClass').then(m => ({ default: m.ArtClass }))),
        route: '/art-class',
        description: 'The ultimate creator studio. Design postcards, stickers, chat bubbles, and more to publish to the store.',
        author: 'vibeman1',
        themeColor: '#FF6B6B',
        visibilityControl: 'user_choice'
    }
};

export const getDynamicModDefinition = (mod: ModDefinition, usersData: any = {}, currentUsername?: string): ModDefinition => {
    if (!mod) return mod;
    
    // 1. Check the CURRENT post author's customizations first!
    if (currentUsername && usersData[currentUsername]) {
        const customMod = (usersData[currentUsername].customMods || []).find((m: any) => m.id === mod.id);
        if (customMod) {
            return {
                ...mod,
                name: customMod.name || mod.name,
                icon: customMod.icon || mod.icon,
                themeColor: customMod.themeColor || mod.themeColor,
                description: (customMod.description !== undefined && customMod.description !== '') ? customMod.description : mod.description
            };
        }
    }
    
    // Fall back to author's custom mod first so renaming/theme/description changes propagate to all users
    if (mod.author && usersData[mod.author]) {
        const authorData = usersData[mod.author];
        const customMod = (authorData.customMods || []).find((m: any) => m.id === mod.id);
        
        if (customMod) {
            return {
                ...mod,
                name: customMod.name || mod.name,
                icon: customMod.icon || mod.icon,
                themeColor: customMod.themeColor || mod.themeColor,
                description: (customMod.description !== undefined && customMod.description !== '') ? customMod.description : mod.description
            };
        }
    }
    
    // Check if current user has a custom icon/name/description override locally
    if (currentUsername && usersData[currentUsername]) {
        const currentUserData = usersData[currentUsername];
        const userCustomMod = (currentUserData.customMods || []).find((m: any) => m.id === mod.id);
        if (userCustomMod) {
            return {
                ...mod,
                name: userCustomMod.name || mod.name,
                icon: userCustomMod.icon || mod.icon,
                themeColor: userCustomMod.themeColor || mod.themeColor,
                description: (userCustomMod.description !== undefined && userCustomMod.description !== '') ? userCustomMod.description : mod.description
            };
        }
    }
    
    return mod;
};

export const getActiveMods = (activeModIds: string[] = [], usersData: any = {}, currentUsername?: string) => {
    return activeModIds
        .map(id => {
            if (MOD_REGISTRY[id]) {
                return getDynamicModDefinition(MOD_REGISTRY[id], usersData, currentUsername);
            }
            // Look for this mod in customMods of any user (typically the current user or author)
            for (const username of Object.keys(usersData || {})) {
                const customMod = (usersData[username]?.customMods || []).find((m: any) => m.id === id);
                if (customMod) {
                    return {
                        id: customMod.id,
                        name: customMod.name,
                        icon: customMod.icon || 'https://api.dicebear.com/7.x/bottts/svg?seed=mod',
                        description: (customMod.description !== undefined && customMod.description !== '')
                            ? customMod.description
                            : (customMod.url ? `Custom web app mod: ${customMod.url}` : 'Custom system mod generated by GoodieBoy.'),
                        author: username,
                        url: customMod.url,
                        themeColor: customMod.themeColor || '#000000',
                        component: React.lazy(() => Promise.resolve({ default: () => null })) // dummy component for web mods
                    };
                }
            }
            return null;
        })
        .filter(mod => !!mod) as ModDefinition[];
};

export const getAllMods = (usersData: any = {}, currentUsername?: string) => {
    const registryMods = Object.values(MOD_REGISTRY).map(mod => getDynamicModDefinition(mod, usersData, currentUsername));
    
    // Also include any user-created custom mods across all users
    const customMods: ModDefinition[] = [];
    for (const username of Object.keys(usersData || {})) {
        const userCustomMods = usersData[username]?.customMods || [];
        for (const cm of userCustomMods) {
            if (!registryMods.some(rm => rm.id === cm.id) && !customMods.some(m => m.id === cm.id)) {
                customMods.push({
                    id: cm.id,
                    name: cm.name,
                    icon: cm.icon || 'https://api.dicebear.com/7.x/bottts/svg?seed=mod',
                    description: (cm.description !== undefined && cm.description !== '')
                        ? cm.description
                        : (cm.url ? `Custom web app mod: ${cm.url}` : 'Custom system mod generated by GoodieBoy.'),
                    author: username,
                    url: cm.url,
                    themeColor: cm.themeColor || '#000000',
                    component: React.lazy(() => Promise.resolve({ default: () => null }))
                });
            }
        }
    }
    
    return [...registryMods, ...customMods];
};

export interface ModBadgeData {
    title: string;
    icon: any;
    fallbackIconName: 'footprint' | 'bell';
    themeColor: string;
    onClickEvent: string;
    onClickDetail: any;
}

export const getModBadgesForPost = (post: any, usersData: any, author: string): ModBadgeData[] => {
    const badges: ModBadgeData[] = [];
    
    if (post.footprintDetails) {
        const mod = getDynamicModDefinition(MOD_REGISTRY['vibeman1_footprint'], usersData, author);
        badges.push({
            title: post.footprintDetails.achievementTitle,
            icon: mod?.icon,
            fallbackIconName: 'footprint',
            themeColor: mod?.themeColor || '#000000',
            onClickEvent: 'app.navigateToFootprint',
            onClickDetail: { taskId: post.footprintDetails.achievementId }
        });
    }
    
    if (post.bzztDetails) {
        const mod = getDynamicModDefinition(MOD_REGISTRY['todo'], usersData, author);
        badges.push({
            title: post.bzztDetails.taskTitle,
            icon: mod?.icon,
            fallbackIconName: 'bell',
            themeColor: mod?.themeColor || '#000000',
            onClickEvent: 'app.navigateToBzzt',
            onClickDetail: { taskId: post.bzztDetails.taskId }
        });
    }

    if (post.originModId && MOD_REGISTRY[post.originModId]) {
        const mod = getDynamicModDefinition(MOD_REGISTRY[post.originModId], usersData, author);
        badges.push({
            title: mod.name,
            icon: mod.icon,
            fallbackIconName: 'bell',
            themeColor: mod.themeColor || '#8b5cf6',
            onClickEvent: `app.handleModClick`,
            onClickDetail: { modId: post.originModId }
        });
    }

    return badges;
};

export const isVideoIcon = (url?: string): boolean => {
    if (!url || typeof url !== 'string') return false;
    return /\.(mp4|webm|ogg|mov|m4v)(?:\?|$)/i.test(url) || url.includes('#video');
};

export const ModIcon: React.FC<{ icon: any; className?: string; style?: React.CSSProperties }> = ({ 
    icon, 
    className = "w-full h-full object-cover", 
    style 
}) => {
    if (!icon) return null;
    if (typeof icon === 'string') {
        if (isVideoIcon(icon)) {
            return React.createElement('video', {
                src: icon,
                className: className,
                style: style,
                autoPlay: true,
                loop: true,
                muted: true,
                playsInline: true,
                preload: "metadata"
            });
        }
        return React.createElement('img', {
            src: icon,
            alt: "",
            className: className,
            style: style
        });
    }
    const IconComp = icon;
    return React.createElement(IconComp, { className: className, style: style });
};
